import { useRef, useState } from 'react';
import { HexColorInput } from 'react-colorful';
import { useTranslation } from 'react-i18next';

import { Form, Formik } from 'formik';

import { Input, Modal, SingleSelect } from '@/common';
import SpinnerInput from '@/common/SpinnerInput';
import { Checkbox, ColorPicker, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import TimeInput from '@/components/generic/TimeInput/TimeInput';
import { queryClient } from '@/configuration/react-query';
import { showToast, ToastType } from '@/helpers/toast';
import { createEditTextOverlayValidation } from '@/pages/VideoTheme/TextOverlay/Validation';
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  ArrowDownHeavyIcon,
  ArrowUpHeavyIcon,
  MarginBottom,
  MarginLeft,
  MarginRight,
  MarginTop
} from '@/resources';
import {
  useCreateVideoThemeTextMutation,
  useUpdateVideoThemeTextMutation
} from '@/services/videoThemeText.service';
import { HexColor, SelectOptionProps } from '@/types';
import { CreateEditVideoThemeText, VideoThemeText } from '@/types/VideoThemeTypes';

type CreateEditTextProps = {
  isOpen: boolean;
  onClose: () => void;
  isCreate: boolean;
  videoThemeText?: VideoThemeText;
  videoThemeId?: number;
};

export const CreateEditText = ({
  isOpen,
  onClose,
  isCreate,
  videoThemeText,
  videoThemeId
}: CreateEditTextProps) => {
  const [t] = useTranslation('common');
  const [isColorPickerShown, setIsColorPickerShown] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const getAlignmentOptions: SelectOptionProps[] = [
    { value: '0', label: 'Left', icon: <AlignLeftIcon /> },
    { value: '1', label: 'Center', icon: <AlignCenterIcon /> },
    { value: '2', label: 'Right', icon: <AlignRightIcon /> }
  ];

  const { mutate: createVideoThemeText } = useCreateVideoThemeTextMutation(queryClient, {
    onSuccess: () => {
      onClose();
      showToast(ToastType.SUCCESS, t('videoThemeTextCreated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const { mutate: updateVideoThemeText } = useUpdateVideoThemeTextMutation(queryClient, {
    onSuccess: () => {
      onClose();
      showToast(ToastType.SUCCESS, t('videoThemeTextSuccessfullyUpdated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const getInitialValues = () => {
    const initialValues: CreateEditVideoThemeText = {
      id: videoThemeText?.id,
      videoThemeId: videoThemeId,
      fontFamily: videoThemeText?.fontFamily || '',
      fontSize: videoThemeText?.fontSize,
      textColor: videoThemeText?.textColor || '#fff',
      alignment: videoThemeText?.alignment,
      marginTop: videoThemeText?.marginTop || 0,
      marginBottom: videoThemeText?.marginBottom || 0,
      marginLeft: videoThemeText?.marginLeft || 0,
      marginRight: videoThemeText?.marginRight || 0,
      startTime: videoThemeText?.startTime,
      endTime: videoThemeText?.endTime,
      timeFromStart: videoThemeText?.timeFromStart || false,
      transitionTime: videoThemeText?.transitionTime || 0,
      value: videoThemeText?.value || ''
    };

    return initialValues;
  };

  const handleSubmit = (values: CreateEditVideoThemeText) => {
    setIsSubmitting(true);
    const payload = {
      ...values
    };

    if (isCreate) {
      createVideoThemeText(payload);
    } else {
      updateVideoThemeText(payload);
    }
  };

  const getAlignmentDefaultValue = (alignment: number | undefined) => {
    return alignment === undefined
      ? null
      : (getAlignmentOptions.find((option) => option?.value === alignment.toString()) ?? null);
  };

  return (
    <Formik
      initialValues={getInitialValues()}
      onSubmit={handleSubmit}
      validationSchema={createEditTextOverlayValidation}>
      {({ errors, touched, values, handleSubmit, setFieldValue, dirty, setFieldTouched }) => (
        <Modal
          isOpen={isOpen}
          height='h-full'
          label={isCreate ? t('addNewText') : t('editText')}
          onClose={onClose}
          modalClasses='md:w-[600px] border border-secondary-blue-gray'
          containerClasses='no-scrollbar'
          hasButton
          hasCancelButton
          buttonText={isCreate ? t('addText') : t('editText')}
          buttonClasses='flex justify-end'
          buttonType={ButtonVariant.PRIMARY}
          isButtonDisabled={isSubmitting || (!isCreate && !dirty) || Object.keys(errors).length > 0}
          handleSubmit={handleSubmit}>
          <Form>
            <div className='w-full border border-solid border-secondary-blue-gray' />
            <div className='my-6 flex w-full flex-row justify-center gap-x-4'>
              <div className='flex w-full flex-col gap-y-4'>
                <Input
                  className='w-full'
                  label={t('value')}
                  isInvalid={!!errors.value && touched.value}
                  errorMessage={touched.value ? errors.value : undefined}
                  id='value'
                  isRequired
                  type='text'
                />
                <div className='flex w-full flex-col gap-y-2 sm:flex-row sm:gap-x-2'>
                  <div className='flex flex-row gap-x-2'>
                    <Input
                      width={'w-full'}
                      placeholder={t('font')}
                      label=''
                      isInvalid={!!errors.fontFamily && touched.fontFamily}
                      id='fontFamily'
                      type='text'
                      isRequired
                    />
                    <Input
                      width={'w-[40%]'}
                      placeholder={t('size')}
                      label=''
                      isInvalid={!!errors.fontSize && touched.fontSize}
                      id='fontSize'
                      isRequired
                      type='number'
                    />
                  </div>
                  <div className='flex flex-row gap-x-2'>
                    <div
                      className='relative flex h-[40px] w-[110px] cursor-pointer items-center justify-between rounded-xl border border-secondary-blue-gray px-5'
                      onClick={() => setIsColorPickerShown(!isColorPickerShown)}
                      ref={divRef}>
                      <div
                        style={{ backgroundColor: values.textColor }}
                        className={'size-6 rounded-full border-2'}
                      />
                      {isColorPickerShown ? (
                        <ArrowUpHeavyIcon className='h-3 w-4 fill-current text-secondary-gray' />
                      ) : (
                        <ArrowDownHeavyIcon className='h-3 w-4 fill-current text-secondary-gray' />
                      )}
                    </div>
                    <div
                      className={`absolute ${errors.value ? 'top-[310px]' : 'top-[270px]'} z-10 sm:right-[70px] ${errors.value ? 'sm:top-[240px]' : 'sm:top-[220px]'}`}>
                      {isColorPickerShown && (
                        <>
                          <ColorPicker
                            color={values.textColor as HexColor}
                            onChange={(value) => setFieldValue('textColor', value)}
                            isOpen={isColorPickerShown}
                            setIsOpen={setIsColorPickerShown}
                            showColorPreview={false}
                          />
                          <HexColorInput
                            color={values.textColor}
                            onChange={(value) => setFieldValue('textColor', value)}
                            className='relative w-full rounded-md border-2 py-1 text-center outline-none'
                            data-prevent-click-outside={true}
                          />
                        </>
                      )}
                    </div>
                    <SingleSelect
                      className='w-36'
                      placeholder={t('alignment')}
                      isInvalid={!!errors.alignment && touched.alignment}
                      id='alignment'
                      isRequired
                      options={getAlignmentOptions}
                      handleChange={(value) => {
                        setFieldValue('alignment', Number(value?.value));
                      }}
                      value={getAlignmentDefaultValue(values.alignment)}
                      onBlur={() => setFieldTouched('alignment', true)}
                    />
                  </div>
                </div>
                <div className='w-full border border-solid border-secondary-blue-gray' />
                <div className='flex w-full flex-col gap-2 sm:flex-row'>
                  <div className='flex w-full flex-col sm:w-[55%]'>
                    <div className='flex w-full flex-row gap-2'>
                      <TimeInput
                        className={'gap-2'}
                        defaultValue={values.startTime}
                        label={t('startTime')}
                        isRequired
                        isInvalid={!!errors.startTime}
                        fieldName='startTime'
                        setFieldValue={setFieldValue}
                      />
                      <TimeInput
                        className={'gap-2'}
                        defaultValue={values.endTime}
                        isInvalid={!!errors.endTime}
                        label={t('endTime')}
                        isRequired
                        fieldName='endTime'
                        setFieldValue={setFieldValue}
                      />
                    </div>
                    <div className='mt-4 flex w-full flex-row gap-2'>
                      <Checkbox
                        id='timeFromStart'
                        label={t('timeFromStart')}
                        onChange={(id) => setFieldValue(id, !values.timeFromStart)}
                        isChecked={values.timeFromStart}
                      />
                    </div>
                    <div className='mt-4 flex w-full flex-row gap-2'>
                      <TimeInput
                        className={'gap-2'}
                        defaultValue={values.transitionTime ? values.transitionTime : 0}
                        label={t('transitionTime')}
                        isRequired
                        isInvalid={!!errors.transitionTime}
                        fieldName='transitionTime'
                        setFieldValue={setFieldValue}
                      />
                    </div>
                  </div>

                  <div className='flex w-full flex-col gap-2 sm:w-[45%]'>
                    <Label text={t('margins')} type={LABEL_TYPE.PARAGRAPH2} />
                    <div className='flex w-full flex-row gap-2'>
                      <SpinnerInput
                        value={values.marginLeft}
                        className='!w-1/2'
                        onChange={(value) => setFieldValue('marginLeft', value)}
                        icon={<MarginLeft />}
                        unit='px'
                      />
                      <SpinnerInput
                        value={values.marginTop}
                        className='!w-1/2'
                        onChange={(value) => setFieldValue('marginTop', value)}
                        icon={<MarginTop />}
                        unit='px'
                      />
                    </div>
                    <div className='flex w-full flex-row gap-2'>
                      <SpinnerInput
                        value={values.marginRight}
                        className='!w-1/2'
                        onChange={(value) => setFieldValue('marginRight', value)}
                        icon={<MarginRight />}
                        unit='px'
                      />
                      <SpinnerInput
                        value={values.marginBottom}
                        className='!w-1/2'
                        onChange={(value) => setFieldValue('marginBottom', value)}
                        icon={<MarginBottom />}
                        unit='px'
                      />
                    </div>
                  </div>
                </div>
                <div className='w-full border border-solid border-secondary-blue-gray' />
              </div>
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
